import { Box, ListItemText } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { SalesOrderFreightRemovalDialog } from "./SalesOrderFreightRemovalDialog"

import { ValueOrMultiple } from "@/components/common/ValueOrMultiple"
import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"
import { FormDatePickerWithCustomActions } from "@/components/UiKit/FormControls/FormDatePickerWithCustomActions"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { Incoterms, IncotermsDefinitionMap, SalesOrder } from "@/graphql/codegen/graphql"
import {
  SALES_ORDER_FREIGHT_TERMS_OPTIONS,
  SALES_ORDER_SHIPMENT_STATUS_OPTIONS,
} from "@/screens/CustomerOrders/constants"
import { useFreightLine } from "@/screens/CustomerOrders/hooks/useFreightLine"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import { isSalesOrderEditable } from "@/screens/CustomerOrders/utils"
import { useGetIncotermsQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/deliveryTerms/useGetIncotermsQuery"
import { useGetShipViaQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/useGetShipViaQuery"
import { useGetPurchaseOrderIncotermsLocations } from "@/screens/Procurement/PurchaseOrder/hooks/useGetPurchaseOrderIncotermsLocations"
import { AddressFormat } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

interface SalesOrderLogisticsFormProps {
  salesOrder: SalesOrder
  onRequestedShipOnChange: (value: string | null) => void
  onRequestedDeliveryDateChange: (value: string | null) => void
  onAsapSelected: () => void
}

export const SalesOrderLogisticsForm: FC<SalesOrderLogisticsFormProps> = ({
  salesOrder,
  onRequestedShipOnChange,
  onRequestedDeliveryDateChange,
  onAsapSelected,
}) => {
  const { control } = useFormContext<SalesOrderLogisticsFormValues>()
  const { isReadonly: isPermissionReadonly } = useSalesOrderPermissions()
  const isReadonly = isPermissionReadonly || !isSalesOrderEditable(salesOrder.status)

  const {
    isRemovalConfirmationOpen,
    handleFreightTermsChange,
    handleFreightRemovalConfirm,
    handleFreightRemovalCancel,
  } = useFreightLine()

  const { data: incotermsData } = useGetIncotermsQuery()
  const incotermsOptions: Option<Incoterms>[] = incotermsData
    .filter((incoterm): incoterm is IncotermsDefinitionMap & { key: Incoterms } => Boolean(incoterm.key))
    .map((incoterm) => ({ label: incoterm.key, value: incoterm.key }))

  const { locations: incotermsLocations } = useGetPurchaseOrderIncotermsLocations()
  const incotermsLocationOptions: Option<string>[] = incotermsLocations.map((location) => ({
    value: location.locationId,
    label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
  }))

  const {
    data: shipViaCompanies,
    fetching: isFetchingShipVia,
    setSearchTerm: setShipViaSearchTerm,
  } = useGetShipViaQuery()
  const shipViaOptions: Option<string>[] = shipViaCompanies.map((company) => ({
    value: company.companyId,
    label: company.name ?? "",
    customLabel: <ListItemText primary={company.name} secondary={company.externalSystemId} />,
  }))

  return (
    <Box className='flex flex-col gap-6' data-testid={`customer-orders.so-logistics-form.${salesOrder.salesOrderId}`}>
      <Box className='flex flex-wrap gap-4'>
        <AerosTextField
          label='Customer PO'
          value=''
          disabled
          slotProps={{
            input: {
              startAdornment: (
                <ValueOrMultiple
                  values={salesOrder.cpoNumbers}
                  className='text-gray-600'
                  data-testid='customer-orders.so-logistics-form.customer-po-value'
                />
              ),
            },
          }}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.customer-po-field'
        />
        <FormTextField
          control={control}
          name='ladingBill'
          label='Bill of Lading'
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.bill-of-lading-field'
        />
        <FormDatePicker
          control={control}
          name='confirmationSentOn'
          label='Confirmation Sent'
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.confirmation-sent-field'
        />
        <FormDatePicker
          control={control}
          name='promisedShipOn'
          label='Promised Ship Date'
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.promised-ship-date-field'
        />
        <FormDatePickerWithCustomActions
          control={control}
          name='requestedShipOn'
          label='Requested Ship Date'
          allowAsap
          onChange={onRequestedShipOnChange}
          onAsapSelect={onAsapSelected}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.requested-ship-date-field'
        />
        <FormDatePickerWithCustomActions
          control={control}
          name='deliveryDate'
          label='Requested Delivery Date'
          allowAsap
          onChange={onRequestedDeliveryDateChange}
          onAsapSelect={onAsapSelected}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.requested-delivery-date-field'
        />
        <FormDatePicker
          control={control}
          name='warehouseShippedOn'
          label='Warehouse Shipped Date'
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.warehouse-shipped-date-field'
        />
      </Box>

      <Box className='flex flex-wrap gap-4'>
        <FormSelect
          control={control}
          name='incoterms'
          label='Incoterms'
          placeholder='Select...'
          options={incotermsOptions}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.incoterms-field'
        />
        <FormAutocomplete
          control={control}
          name='incotermsLocationId'
          label='Incoterms Location'
          placeholder='Select...'
          options={incotermsLocationOptions}
          disabled={isReadonly}
          className='min-w-72 flex-1'
          data-testid='customer-orders.so-logistics-form.incoterms-location-field'
        />
        <FormAutocomplete
          control={control}
          name='shipViaCompanyId'
          label='Ship Via'
          placeholder='Search and select...'
          options={shipViaOptions}
          disabled={isReadonly || isFetchingShipVia}
          onInputChange={setShipViaSearchTerm}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.ship-via-field'
        />
        <FormSelect
          control={control}
          name='freightTerms'
          label='Freight Terms'
          placeholder='Select...'
          options={SALES_ORDER_FREIGHT_TERMS_OPTIONS}
          onChange={handleFreightTermsChange}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.freight-terms-field'
        />
        <FormSelect
          control={control}
          name='shipmentStatus'
          label='Shipment Status'
          placeholder='Select...'
          options={SALES_ORDER_SHIPMENT_STATUS_OPTIONS}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.shipment-status-field'
        />
        <FormTextField
          control={control}
          name='trackingNumber'
          label='Tracking / PRO #'
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.tracking-number-field'
        />
      </Box>

      <Box className='flex flex-wrap gap-4'>
        <FormTextField
          control={control}
          name='bolNotes'
          label='BoL Notes'
          multiline
          rows={2}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.bol-notes-field'
        />
        <FormTextField
          control={control}
          name='pickTicketNotes'
          label='Pick Ticket Notes'
          multiline
          rows={2}
          disabled={isReadonly}
          className='min-w-40 flex-1'
          data-testid='customer-orders.so-logistics-form.pick-ticket-notes-field'
        />
      </Box>

      {isRemovalConfirmationOpen && (
        <SalesOrderFreightRemovalDialog onClose={handleFreightRemovalCancel} onConfirm={handleFreightRemovalConfirm} />
      )}
    </Box>
  )
}
